import { useCallback, useEffect, useState } from "react";
import { getDevices } from "services/api";

const STORAGE_KEY = "padihat_selected_device";

// storageKey lets a page own its selection instead of sharing the app-wide
// one. The Experiment page does: it used to share this key with the Camera
// tab, so opening a camera's images there silently overwrote the Experiment
// page's water-level device with the camera (a real bug found live
// mid-experiment) - see EXPERIMENT_WATER_STORAGE_KEY in pages/Experiment.jsx.
export const useDevices = ({ storageKey = STORAGE_KEY } = {}) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDeviceState] = useState(
    () => localStorage.getItem(storageKey) || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setSelectedDevice = (mac) => {
    setSelectedDeviceState(mac);
    if (mac) localStorage.setItem(storageKey, mac);
    else localStorage.removeItem(storageKey);
  };

  // useCallback with an empty dep array - a stable reference so callers
  // (e.g. Experiment.jsx's own poll interval) can safely depend on
  // `refetch` without it changing identity every render and re-arming
  // their effect. Doesn't close over any props/reactive state - setters
  // are stable and storageKey is fixed for a hook instance's lifetime - so
  // [storageKey] is effectively [], not a lint-silencing shortcut.
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDevices();
      const sorted = [...data.devices].sort((a, b) =>
        (a.name || a.mac_address).localeCompare(b.name || b.mac_address)
      );
      setDevices(sorted);
      const stored = localStorage.getItem(storageKey);
      const stillExists = sorted.some((d) => d.mac_address === stored);
      if (!stillExists && sorted.length > 0) {
        setSelectedDevice(sorted[0].mac_address);
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { devices, selectedDevice, setSelectedDevice, loading, error, refetch: fetchDevices };
};
